import PageShell from "@/components/site/PageShell";
import Pricing from "@/components/site/Pricing";
import FinalCTA from "@/components/site/FinalCTA";
import { motion } from "framer-motion";
import { ShieldCheck, Clock, BadgeCheck } from "lucide-react";

const HIGHLIGHTS = [
  { icon: ShieldCheck, label: "No hidden fees", desc: "Flat-rate pricing, transparent contracts." },
  { icon: Clock, label: "Fast turnaround", desc: "Websites ship in 3–6 weeks." },
  { icon: BadgeCheck, label: "Performance backed", desc: "We track KPIs that move revenue." },
];

const FAQS = [
  {
    q: "Can I switch between SEO plans later?",
    a: "Absolutely — you can upgrade at any time and we'll pro-rate the difference. Downgrades take effect at the next billing cycle.",
  },
  {
    q: "What does the 12-month SEO agreement cover?",
    a: "SEO is a compounding investment. Twelve months gives our team enough runway to deliver measurable ranking and lead growth that lasts beyond the engagement.",
  },
  {
    q: "Do website projects include hosting?",
    a: "Hosting is not included by default — but we'll set you up on the right platform and can manage hosting + maintenance under a separate care plan if you'd like.",
  },
  {
    q: "Is there a setup fee for monthly SEO?",
    a: "No setup fee. Your first month already includes our full onboarding, audit and roadmap so you're never paying twice.",
  },
];

export default function PricingPage() {
  return (
    <PageShell testId="pricing-page">
      <section className="relative pt-16 pb-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Pricing &amp; Packages
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Premium growth,{" "}
              <span className="text-gradient-brand">priced honestly.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              Flat-rate websites and predictable monthly SEO retainers — designed
              to deliver more rankings, more leads and more revenue without the
              agency runaround.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={h.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.025] px-5 py-5 text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600/20 to-cyan-400/10 border border-white/10 flex items-center justify-center">
                  <h.icon className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="mt-3 font-semibold">{h.label}</div>
                <div className="text-sm text-white/55 mt-0.5">{h.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Pricing />

      {/* Pricing FAQ */}
      <section className="section relative" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Pricing FAQ
            </div>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight">
              Questions about pricing.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={f.q}
                data-testid={`pricing-faq-${i}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.025] open:border-cyan-500/30 transition-colors"
              >
                <summary className="list-none cursor-pointer px-6 py-5 flex items-center justify-between gap-3 font-display text-lg font-semibold">
                  {f.q}
                  <span className="text-cyan-400 transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                </summary>
                <div className="px-6 pb-5 text-white/65 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
